import {
  VALUE_LIST_LIMIT,
  matchesColumnFilter,
  type ColumnFilter,
  type ColumnFilters,
  type FilterCellValue,
  type FilterFieldKind,
} from '@/components/filters/column-filter'
import { diffKg, issueCodes, type ReconcileRecord } from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'

const t = messages.reconcile

interface FilterableColumn {
  kind: FilterFieldKind
  /** Süzülen ham değer (ekrandaki biçim değil): tarih günü, ton sayısı, boş = `null`. */
  value: (record: ReconcileRecord) => FilterCellValue
  /**
   * Çok değerli kolon: değer listesi öğeleri tek tek sunar, öğelerinden biri seçili olan
   * kayıt geçer (koşullar `value` metnine uygulanır).
   */
  items?: (record: ReconcileRecord) => string[]
}

/** Kodlar adlarıyla ("H-A İrsaliye kesilmemiş"): filtre listesinde ne olduğu görünsün. */
const codeNames = (record: ReconcileRecord) =>
  issueCodes(record).map((code) => `${code} ${t.codes[code]}`)

/** Kayıt tablosunun başlık hunisi olan kolonları; anahtarlar kolon kimlikleri. */
export const FILTERABLE_COLUMNS: Record<string, FilterableColumn> = {
  status: { kind: 'text', value: (record) => t.status[record.status] },
  codes: { kind: 'text', value: (record) => codeNames(record).join(', '), items: codeNames },
  kantar: { kind: 'text', value: (record) => record.kantarAdi },
  tartino: { kind: 'numeric', value: (record) => record.tartino },
  tarih: { kind: 'date', value: (record) => record.tarih },
  giris: { kind: 'datetime', value: (record) => record.giris },
  cikis: { kind: 'datetime', value: (record) => record.kantar?.cikis ?? null },
  plaka: { kind: 'text', value: (record) => record.plaka },
  kantarTon: { kind: 'numeric', value: (record) => record.kantarTon },
  diaTon: { kind: 'numeric', value: (record) => record.diaTon },
  diff: { kind: 'numeric', value: diffKg },
  cari: { kind: 'text', value: (record) => record.cari },
  note: { kind: 'text', value: (record) => record.override?.note ?? null },
}

export const COLUMN_FILTER_KINDS: ReadonlyMap<string, FilterFieldKind> = new Map(
  Object.entries(FILTERABLE_COLUMNS).map(([id, column]) => [id, column.kind]),
)

function matches(record: ReconcileRecord, column: FilterableColumn, filter: ColumnFilter) {
  if (column.items && filter.type === 'values') {
    const items = column.items(record)
    if (items.length === 0) return filter.blank
    const picked = filter.mode === 'in'
    return items.some((item) => filter.values.includes(item) === picked)
  }
  return matchesColumnFilter(column.value(record), column.kind, filter)
}

/** Kolon filtreleri arasında VE; `except` (değer listesi açılan kolon) hariç, Excel gibi. */
export function applyColumnFilters(
  records: ReconcileRecord[],
  filters: ColumnFilters,
  except?: string,
): ReconcileRecord[] {
  const active = Object.entries(filters).flatMap(([id, filter]) => {
    const column = FILTERABLE_COLUMNS[id]
    return column && id !== except ? [{ column, filter }] : []
  })
  if (active.length === 0) return records
  return records.filter((record) =>
    active.every(({ column, filter }) => matches(record, column, filter)),
  )
}

/** Başlık hunisinin değer listesi: boşlar hariç, aramaya uyan, sıralı, en fazla sınır kadar. */
export function distinctValues(
  records: ReconcileRecord[],
  columnId: string,
  search: string,
): string[] {
  const column = FILTERABLE_COLUMNS[columnId]
  if (!column) return []
  const needle = search.toLocaleLowerCase('tr')
  const values = new Set<string>()
  for (const record of records) {
    for (const value of column.items ? column.items(record) : [column.value(record)]) {
      if (value === null || value === undefined || value === '') continue
      const text = String(value)
      if (needle && !text.toLocaleLowerCase('tr').includes(needle)) continue
      values.add(text)
    }
  }
  return [...values]
    .sort((a, b) => a.localeCompare(b, 'tr', { numeric: true }))
    .slice(0, VALUE_LIST_LIMIT)
}

const datasetIds = new WeakMap<object, number>()
let lastDatasetId = 0

/** Değer listesi sorgu anahtarı için kayıt dizisinin kimliği (dizi değişince liste tazelenir). */
export function datasetId(records: ReconcileRecord[]): number {
  let id = datasetIds.get(records)
  if (id === undefined) {
    id = ++lastDatasetId
    datasetIds.set(records, id)
  }
  return id
}
